import React, { useEffect, useState, useRef, ChangeEvent } from 'react';

import { Alert, Form, Glyphicon } from 'react-bootstrap';
import { AxiosError } from 'axios';

import CollapsableItem from '../utils/CollapsableItem';
import { isMobileResolution } from '../../infrastructure/utils/common';
import DragnDropTable from '../utils/DragnDrop/DragnDropTable';
import { ExploreConfigurationElement } from '../cityExplore/ExploreConfigurationElement';
import { ExploreConfigurationForm } from '../cityExplore/ExploreConfigurationForm';
import Modal from '../utils/ModalComponet';
import Spinner from '../utils/Spinner';
import TopPanel from '../utils/TopPanel';
import RouterLeavingGuard from '../routing/RouteLeavingGuard';
import Succsess from '../stateMessage/Succsess';
import PhotoWidget from '../utils/FormWidgets/PhotoWidget';
import GroupedFormField from '../utils/FormWidgets/FormGroup';
import { updateCityField } from '../../api/ActivitiesApi';
import { City } from '../../types/City';

type Props = {
  cityObj: City;
  history: any;
  cityName: string;
  radiusRequestState: string;
  actions: {
    updateCityExplorePicture(file: File, name: string): string;
    update(city: City): void;
  };
  isDefaultCity: boolean;
  onHandleShownRadiusFiled(): void;
  isShownRadiusField: boolean;
};
export const CityExploreForm = ({
  cityObj,
  history,
  cityName,
  actions,
  radiusRequestState,
  isDefaultCity,
  onHandleShownRadiusFiled,
  isShownRadiusField,
}: Props) => {
  const [city, setCity] = useState<City>(cityObj);
  const [modalContent, setModalContent] = useState<JSX.Element | undefined>(
    undefined
  );
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [backpage, setBackpage] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [changing, setChanging] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMsg, setAlertMsg] = useState<string>('');

  const updatableElement = useRef(undefined);
  const elementIndex = useRef(-1);

  const isMobile = isMobileResolution();
  const isExpanded = !isMobile;

  const { radius = 0 } = city ?? { radius: undefined };

  const validateRadius = !isShownRadiusField || radius >= 0;

  const insertRandomizeProp = configs =>
    configs.map(config =>
      config.isRandomized === undefined
        ? { ...config, isRandomized: false }
        : config
    );

  useEffect(() => {
    if (cityObj) {
      const cityObjWithRandomizedProp = {
        ...cityObj,
        exploreConfigurations: insertRandomizeProp(
          cityObj.exploreConfigurations
        ),
      };
      setCity(cityObjWithRandomizedProp);
      setLoading(false);
    }
  }, [cityObj]);

  useEffect(() => {
    if (history.goBack) {
      setBackpage(true);
    }
  }, [history]);

  const changeExploreState = data => {
    setChanging(true);
    const newState = {
      ...city,
      ...{ exploreConfigurations: data.exploreConfigurations },
    };
    setCity(newState as City);
    return newState;
  };

  const elementSubmit = (element, addMore, receiver) => {
    if (elementIndex.current === -1) {
      changeExploreState({
        [receiver]: [...city[receiver], element],
      });
    } else {
      city[receiver][elementIndex.current] = element;
      changeExploreState({
        [receiver]: [...city[receiver]],
      });
    }
    setOpenModal(false);
    elementIndex.current = -1;
  };

  const exploreConfigurations = () => (
    <ExploreConfigurationForm
      onSubmit={elementSubmit}
      cityExplore={updatableElement.current}
      isOOMConfig={isDefaultCity}
    />
  );

  const deleteElement = (index, receiver) => {
    const id = parseInt(index, 10);
    city[receiver][id] = undefined;
    changeExploreState({
      [receiver]: Array.isArray(city[receiver])
        ? city[receiver].filter(elem => elem)
        : undefined,
    });
  };

  const toElementManagement = (index, receiver) => {
    const id = parseInt(index, 10);
    const element = id > -1 ? city[receiver][id] : undefined;

    elementIndex.current = id;
    updatableElement.current = element;
    setModalContent(exploreConfigurations);
    setOpenModal(true);
    setModalAction(undefined);
  };

  const cityExploreActions = {
    deleteCityExplore: deleteElement,
    updateCityExplore: toElementManagement,
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const changeSavedStatus = () => {
    setIsSaved(false);
    setChanging(true);
  };

  const handleSubmit = async newCityState => {
    try {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      const newCity = ((await actions.update(newCityState)) as unknown) as City;
      if (newCity?.response) {
        throw newCity.response;
      }

      setCity(newCity);
      setIsSaved(true);
      setTimeout(changeSavedStatus, 5000);
    } catch (e) {
      setShowAlert(true);
      const error = e as AxiosError;
      setAlertMsg(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    }
    setSaving(false);
    setChanging(false);
  };

  const onSave = async () => {
    setSaving(true);
    const { radius } = city;
    if (radius) {
      await updateCityField({ radius }, city.name);
    }
    await handleSubmit({ ...city, radius: undefined });
  };

  const goBack = () => {
    history.goBack();
  };

  const savingText = saving ? 'Saving....' : 'Save';

  const onPictureUploadHandle = async event => {
    const { target } = event;
    if (target.files.length === 0) {
      return;
    }
    if (
      target.files[0].type !== 'image/png' &&
      target.files[0].type !== 'image/jpeg'
    ) {
      setAlertMsg('Unable image type');
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
        setAlertMsg('');
      }, 500);
      return;
    }
    const url = await actions.updateCityExplorePicture(
      target.files[0],
      cityName
    );

    setCity({
      ...city,
      explorePicture: url,
    });
  };

  const onRadiusChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value: radius },
    } = event;
    setCity({ ...city, radius: +radius });
  };

  return loading ? (
    <Spinner />
  ) : (
    <>
      {isSaved && <Succsess text='Saving completed' />}
      {showAlert && (
        <Alert className='alert-danger'>
          <p>{alertMsg}</p>
        </Alert>
      )}
      <Modal
        open={openModal}
        closeModal={closeModal}
        action={modalAction}
        actionButtonText=''
      >
        {modalContent}
      </Modal>
      <TopPanel isDetailsPage>
        <div
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          type='button'
          role='button'
          tabIndex={0}
          className='btn-nav back'
          onClick={() => (backpage ? goBack() : undefined)}
          onKeyPress={() => (backpage ? goBack() : undefined)}
          style={{ marginLeft: '15px' }}
          aria-label='left Align'
        >
          {backpage && (
            <>
              <i
                className='fas fa-arrow-left'
                style={{ paddingRight: '8px' }}
              />
              <span className='page-back'>Back</span>
            </>
          )}
          <span className='page-back-mobile'>{cityName}</span>
        </div>
        <div className='page-actions'>
          <button
            type='button'
            disabled={saving || !validateRadius}
            onClick={onSave}
            style={{ marginRight: '0px' }}
            className='btn btn-primary pull-right'
          >
            {savingText}
          </button>
        </div>
      </TopPanel>
      <div className='data-container'>
        <Form>
          <div className='data-box'>
            <div className='media'>
              <PhotoWidget
                header='Photo'
                onChange={onPictureUploadHandle}
                full
                width='120px'
                height='120px'
                fileStatus=''
                image={{
                  isNew: city?.explorePicture?.startsWith('data:image'),
                  // eslint-disable-next-line no-nested-ternary
                  data: city?.explorePicture
                    ? city?.explorePicture?.startsWith('https://ik.imagekit.io')
                      ? `${city?.explorePicture}?tr=w-600`
                      : city?.explorePicture
                    : '',
                }}
              />
            </div>
          </div>

          {!isDefaultCity && !isShownRadiusField && (
            <button
              type='button'
              onClick={onHandleShownRadiusFiled}
              className='btn btn-def'
              style={{ marginRight: '0px', marginTop: 16 }}
            >
              + Add Radius
            </button>
          )}
          {!isDefaultCity && isShownRadiusField && (
            <>
              {radiusRequestState === 'in_progress' ? (
                <Glyphicon
                  key={0}
                  glyph='refresh'
                  className='spinning'
                  style={{ fontSize: '14px' }}
                />
              ) : (
                <GroupedFormField
                  title='Radius (in miles)'
                  placeholder='Radius'
                  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
                  // @ts-ignore
                  onChange={onRadiusChange}
                  value={radius}
                  type='number'
                  min={0}
                  max={999}
                />
              )}
            </>
          )}
          <div className='data-box'>
            <h4 className='header' style={{ paddingLeft: '0px' }}>
              City Explore
            </h4>
            <button
              type='button'
              onClick={() => toElementManagement('-1', 'exploreConfigurations')}
              className='btn btn-def'
              style={{ marginRight: '0px' }}
            >
              + Add New
            </button>
            <div className='clearfix' />
            {city?.exploreConfigurations?.length > 0 && (
              <CollapsableItem isExpanded={isExpanded}>
                <DragnDropTable
                  // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
                  // @ts-ignore
                  content={city.exploreConfigurations}
                  onChange={changeExploreState}
                  headers={[
                    'Title',
                    'Subtitle',
                    'Tag',
                    'Randomized?',
                    'Global?',
                    'Actions',
                  ]}
                  notHeader={isMobile}
                  actions={cityExploreActions}
                  receiver='exploreConfigurations'
                  Element={ExploreConfigurationElement}
                />
              </CollapsableItem>
            )}
          </div>
          <RouterLeavingGuard
            when={changing}
            shouldBlockNavigation={() => changing}
          />
          <div className='page-actions-mobile' style={{ padding: '20px 10px' }}>
            <button
              type='button'
              disabled={saving}
              onClick={onSave}
              className='btn btn-primary pull-right'
            >
              {savingText}
            </button>
          </div>
        </Form>
      </div>
    </>
  );
};
